import * as React from 'react';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import LanguageIcon from '@material-ui/icons/Language';
import ViewIcon from '@material-ui/icons/Visibility';
import { Field, Form as FinalForm } from 'react-final-form';
import { connect } from 'react-redux';

import { Loader } from 'components/loader/Loader';
import { InlineError } from 'components/inline-error/InlineError';
import localStorage from 'core/local-storage/localStorage';
import { IReduxStoreState } from 'core/store/interfaces';

import { TranslateDialog } from './components/TranslateDialog/TranslateDialog';
import { IProps, IState } from './interfaces';
import { permissionsConfig } from './permissionsConfig';
import styles from './Form.module.styl';
import { Tooltip } from '@material-ui/core';
import { checkSlugs } from 'pages/admin/articles-form/redux';
import { slugNormalizer } from 'core/helpers/normalizers';
import { toast } from 'react-toastify';

const IDENTITY_EXCLUDED = ['title', 'name'];
const identity = (isIdent: boolean = false) => (value: any) => {
  if (!isIdent && typeof value === 'string') {
    return value.trim();
  }
  return value;
};

export class Form extends React.Component<IProps, IState> {
  public constructor(props: IProps) {
    super(props);

    this.state = {
      englishTranslation: props.initialValues,
      initialValues: props.initialValues,
      isDirty: false,
      isTranslateDialogOpen: false,
    };
  }

  public render() {
    const { validate, initialValues } = this.props;
    const { englishTranslation } = this.state;

    return (
      <div className={styles.root}>
        <FinalForm
          onSubmit={this.onSubmit}
          validate={validate}
          initialValues={{
            ...initialValues,
            ...englishTranslation,
          }}
          render={this.renderedForm}
          decorators={this.props.decorators}
        />
      </div>
    );
  }

  private onSubmit = (values: any) => {
    const { onSubmit, withTranslation } = this.props;

    if (!withTranslation) {
      return onSubmit(values, this.resetState);
    }

    return onSubmit(
      {
        ...values,
        translation: this.state.initialValues.translation || {},
      },
      this.resetState,
    );
  };

  private resetState = () => {
    this.setState({
      englishTranslation: {},
      initialValues: {},
      isDirty: false,
    });
  };

  private readonly handleTranslation = (data: any) => {
    const { config } = this.props;

    const translatableFields = config.fields
      .filter((field: any) => field.translatable)
      .reduce((acc: any, item: any) => {
        acc[item.name] = item;
        return acc;
      }, {} as any);

    const values = Object.entries(data)
      .filter(([key]) => translatableFields[key])
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as any);

    if (data.language === 'en') {
      this.setState({
        englishTranslation: values,
      });
    }

    this.setState({
      initialValues: {
        ...(this.state.initialValues || {}),
        translation: {
          ...(this.state.initialValues
            ? this.state.initialValues.translation
            : {}),
          [data.language]: values,
        },
      },

      isDirty: true,
    });
  };

  private readonly checkRole = ({ roles }: any) => {
    if (!Array.isArray(roles)) {
      return true;
    }

    const {
      user: { type },
    } = this.props;

    return roles.includes(type);
  };

  private readonly closeTranslationDialog = () => {
    this.setState({
      isTranslateDialogOpen: false,
    });
  };

  private readonly getFields = () => {
    const { config } = this.props;

    const { fields, withPermission } = config;
    return withPermission ? [...fields, ...permissionsConfig] : fields;
  };

  private readonly openPreviewDialog = (values: any) => {
    const { withPreview } = this.props;

    const data = {
      ...values,
      translation: this.state.initialValues.translation,
    };

    localStorage.set('DRAFT', data);
    window.open(`/preview?type=${withPreview}`, '_blank');
  };

  private readonly openTranslateDialog = () => {
    this.setState({
      isTranslateDialogOpen: true,
    });
  };

  private readonly renderedForm = ({
    handleSubmit,
    pristine,
    values,
    form: { restart },
  }: any) => {
    const {
      additionalButtons,
      config,
      errorMessage,
      withPreview,
      withTranslation,
      disableSubmitButton,
      validate,
      showLoader,
    } = this.props;

    const { isDirty, isTranslateDialogOpen } = this.state;
    const onSubmit = async (event: any) => {
      const isSuccess = await handleSubmit(event);
      if (isSuccess) {
        restart();
      }
    };

    const openPreviewDialog = () => this.openPreviewDialog(values);

    return (
      <React.Fragment>
        {withTranslation && isTranslateDialogOpen && (
          <TranslateDialog
            isOpen={isTranslateDialogOpen}
            handleClose={this.closeTranslationDialog}
            config={config}
            initialValues={{ ...this.state.initialValues, en: values }}
            handleTranslation={this.handleTranslation}
            withTranslation={withTranslation}
            validate={validate}
          />
        )}
        <div className={styles.header}>
          <Typography className={styles.title} variant="h4" id="tableTitle">
            {typeof config.title === 'function'
              ? config.title(this.props)
              : config.title}
          </Typography>
          {withTranslation && (
            <IconButton onClick={this.openTranslateDialog}>
              <LanguageIcon />
            </IconButton>
          )}
          {withPreview && (
            <IconButton onClick={openPreviewDialog}>
              <ViewIcon />
            </IconButton>
          )}
        </div>
        {errorMessage && <InlineError>{errorMessage}</InlineError>}
        <form onSubmit={onSubmit}>
          {this.getFields().map((field: any | any[]) => {
            if (Array.isArray(field)) {
              return (
                <div className={styles.inlineFieldsWrapper} key={field[0].name}>
                  {field.map((inlineField: any) => {
                    const inlineRender = (props: any): React.ReactNode => {
                      const {
                        input: { name, onChange },
                      } = props;

                      const onchange = async (value: boolean) => {
                        if (!value && values.slug) {
                          try {
                            const normalizedValue = await slugNormalizer(
                              values.title,
                            );
                            const { data } = await checkSlugs(normalizedValue);

                            if (Array.isArray(data)) {
                              return data[0];
                            }
                          } catch (error) {
                            if (error.message !== '404') {
                              toast.error(error.message);
                            }
                          }
                        }

                        onChange(value);
                      };

                      if (name === 'isSlugNotSynchronized') {
                        props.input.onChange = onchange;
                      }

                      return (
                        <inlineField.component
                          {...props}
                          {...inlineField}
                          {...this.props}
                        />
                      );
                    };

                    return (
                      <Field
                        key={inlineField.name}
                        name={inlineField.name}
                        render={inlineRender}
                        parse={identity(false)}
                      />
                    );
                  })}
                </div>
              );
            }

            if (field.customizedComponent) {
              const customized = field.customizedComponent(this.props);
              const renderedField = (props: any) => (
                <customized.component
                  disabled={!this.checkRole(field) || customized.isDisabled}
                  {...props}
                  {...field}
                  {...this.props}
                />
              );
              if (!customized.showInput) {
                return <></>;
              }
              if (customized.hasTooltip) {
                return (
                  <Tooltip
                    title={customized.tooltipTitle}
                    placement="bottom-start"
                    key={field.name}
                  >
                    <div>
                      <Field
                        name={field.name}
                        render={renderedField}
                        parse={identity(false)}
                      />
                    </div>
                  </Tooltip>
                );
              }
              return (
                <Field
                  key={field.name}
                  name={field.name}
                  render={renderedField}
                  parse={identity(false)}
                />
              );
            }

            const renderedField = (props: any) => (
              <field.component
                disabled={!this.checkRole(field)}
                {...props}
                {...field}
                {...this.props}
              />
            );

            return (
              <Field
                key={field.name}
                name={field.name}
                render={renderedField}
                parse={identity(IDENTITY_EXCLUDED.includes(field.name))}
              />
            );
          })}
          <div className={styles.buttons}>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={this.isDisabled(disableSubmitButton, pristine, isDirty)}
            >
              {config.submitButtonCaption || 'Submit'}
              {showLoader && <Loader sizeValue={20} centrify={false} />}
            </Button>
            {Array.isArray(additionalButtons) && additionalButtons}
          </div>
        </form>
      </React.Fragment>
    );
  };

  private isDisabled(
    disableSubmitButton: any,
    pristine: boolean,
    isDirty: boolean,
  ) {
    return typeof disableSubmitButton === 'function'
      ? disableSubmitButton({ pristine, isDirty })
      : pristine && !isDirty;
  }
}

const mapState = (state: IReduxStoreState) => ({
  ...((state.references.data && state.references.data.items) || {}),
  user: state.user.data && state.user.data.data,
});

export default connect<IReduxStoreState, null, IProps, IReduxStoreState>(
  mapState,
)(Form);
